/**
 * Zentraler Consent-Speicher der Engine.
 *
 * ZWEI GETRENNTE ZWECKE, bewusst NICHT gekoppelt. Art. 4 Nr. 11 DSGVO verlangt
 * eine Einwilligung "für den bestimmten Fall"; ein einziges "Zustimmen", das
 * Reichweitenmessung UND externe Einbettungen gleichzeitig freischaltet, ist
 * weder spezifisch noch informiert. Deshalb:
 *
 *  1. REICHWEITENMESSUNG (Matomo, self-hosted, cookielos). Eine bewusste
 *     Ja/Nein-Entscheidung, die dauerhaft gelten soll → localStorage.
 *  2. EXTERNE EINBETTUNGEN (YouTube, Vimeo, Google Maps). Die Einwilligung
 *     erteilt der Klick auf die jeweilige Fassade — pro Anbieter. "Für diese
 *     Sitzung merken" hält sie nur bis zum Schließen des Tabs →
 *     sessionStorage. Ohne Häkchen wird NICHTS gespeichert.
 *
 * Gespeichert wird jeweils ein Nachweis-Datensatz (Art. 7 Abs. 1: nachweisen
 * können, DASS, WOFÜR und auf welcher Textfassung eingewilligt wurde): Status,
 * Zeitstempel, Textversion. Das ist ein Nachweis AUF DEM GERÄT — für einen
 * serverseitigen Nachweis müsste die Entscheidung zusätzlich protokolliert
 * werden; das wäre ohne Einwilligung selbst wieder eine Datenverarbeitung und
 * ist hier bewusst unterlassen.
 *
 * Weder localStorage noch sessionStorage sind Cookies; der Zugriff darauf ist
 * nach § 25 Abs. 2 Nr. 2 TTDSG einwilligungsfrei, weil er ausschließlich dazu
 * dient, den vom Nutzer gewünschten Dienst (das Merken seiner Entscheidung)
 * bereitzustellen.
 */

// Version des Einwilligungstextes. MUSS hochgezählt werden, sobald sich der
// Banner-Text inhaltlich ändert — alte Entscheidungen beziehen sich dann auf
// eine andere Aussage und müssen neu eingeholt werden.
export const CONSENT_VERSION = 2

const ANALYTICS_KEY = 'wg-consent-analytics'
const EMBED_KEY_PREFIX = 'wg-consent-embed:'

// Vorgänger-Schlüssel des pauschalen "Cookie"-Banners. Diese Zustimmung galt
// einem anderen Text und einem anderen Zweck und darf NICHT stillschweigend
// als Einwilligung in die Reichweitenmessung weiterleben.
const LEGACY_KEY = 'wg-cookie-consent'

export const EVENT_ANALYTICS = 'wg:consent-analytics'
export const EVENT_EMBED = 'wg:consent-embed'

function safeGet(store, key) {
	try {
		return window[store].getItem(key)
	} catch {
		// Storage kann im Privatmodus oder bei blockierten Drittinhalten werfen.
		return null
	}
}

function safeSet(store, key, value) {
	try {
		window[store].setItem(key, value)
		return true
	} catch {
		return false
	}
}

function safeRemove(store, key) {
	try {
		window[store].removeItem(key)
	} catch {
		/* nichts zu tun */
	}
}

/** Altlast entfernen, damit niemand ungefragt als "einwilligend" gilt. */
export function dropLegacyConsent() {
	safeRemove('localStorage', LEGACY_KEY)
}

/**
 * @returns {{status: 'granted'|'denied', ts: string, version: number}|null}
 *   null = noch keine Entscheidung ODER Entscheidung zu einer älteren
 *   Textfassung (dann muss erneut gefragt werden).
 */
export function readAnalyticsDecision() {
	const raw = safeGet('localStorage', ANALYTICS_KEY)
	if (!raw) return null
	try {
		const record = JSON.parse(raw)
		if (record.version !== CONSENT_VERSION) return null
		if (record.status !== 'granted' && record.status !== 'denied') return null
		return record
	} catch {
		return null
	}
}

export function analyticsGranted() {
	const record = readAnalyticsDecision()
	return !!record && record.status === 'granted'
}

export function writeAnalyticsDecision(status) {
	const record = {
		status,
		ts: new Date().toISOString(),
		version: CONSENT_VERSION,
		purpose: 'reichweitenmessung-matomo',
	}
	safeSet('localStorage', ANALYTICS_KEY, JSON.stringify(record))
	window.dispatchEvent(new CustomEvent(EVENT_ANALYTICS, { detail: record }))
	return record
}

/** Anbieter-Schlüssel normalisieren ('youtube' | 'vimeo' | 'maps' | …). */
function embedKey(provider) {
	return EMBED_KEY_PREFIX + (provider || 'extern')
}

export function embedRemembered(provider) {
	return safeGet('sessionStorage', embedKey(provider)) === 'granted'
}

/** Nur aufrufen, wenn der Nutzer "für diese Sitzung merken" aktiv angehakt hat. */
export function rememberEmbed(provider) {
	safeSet(
		'sessionStorage',
		embedKey(provider),
		'granted'
	)
	window.dispatchEvent(new CustomEvent(EVENT_EMBED, { detail: { provider } }))
}

/**
 * Widerruf (Art. 7 Abs. 3 DSGVO — muss so einfach sein wie die Erteilung).
 * Löscht BEIDE Zwecke; der Aufrufer lädt die Seite anschließend neu, damit
 * bereits geladene Embeds verschwinden.
 */
export function resetAllConsent() {
	safeRemove('localStorage', ANALYTICS_KEY)
	dropLegacyConsent()
	try {
		const keys = []
		for (let i = 0; i < window.sessionStorage.length; i += 1) {
			const key = window.sessionStorage.key(i)
			if (key && key.startsWith(EMBED_KEY_PREFIX)) keys.push(key)
		}
		keys.forEach((key) => safeRemove('sessionStorage', key))
	} catch {
		/* Storage nicht verfügbar — dann gibt es auch nichts zu widerrufen. */
	}
}
